import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  type FieldArrayWithId,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
  type UseFieldArrayUpdate,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useIsAllowed } from '../../../../../../../configs/permission/utils/useIsAllowed.ts';
import { MatchingOperator } from '../../../../constants.ts';
import {
  AudienceFields,
  type AudienceForm,
  type Group,
  type GroupIndexFieldName,
  IncludesGroupName,
  ListType,
} from '../../../../types.ts';
import {
  applyMarketForcedUnionOperators,
  type EnforceMarketUnionOperatorsCallOptions,
} from '../../../../utils/applyMarketForcedUnionOperators.ts';
import { extractNonEmptyGroup } from '../../../../utils/extractNonEmptyGroup.ts';
import { isGroupReallyEmpty } from '../../../../utils/isGroupReallyEmpty.ts';
import { ConfirmationModal } from '../../../ConfirmationModal.tsx';
import { OperatorChip, OperatorType } from '../../../DimensionChip/OperatorChip.tsx';
import { type OperatorPath, OperatorSelector } from '../../../StructureAndLogic/components/OperatorSelector.tsx';
import { useDealsRevenueMethod } from '../../../StructureAndLogic/components/useDealsRevenueMethod.ts';
import { audienceLocation, type SelectedLocationSegment } from '../../constants.ts';
import type { DrawerState } from '../../drawerTypes.ts';
import { buildIndexedGroupFieldName } from '../../utils/getGroupFromPath.ts';
import { AddButton } from '../AddButton.tsx';
import { ItemContainer } from '../ItemContainer.tsx';
import {
  generateInitialSelectedLocationState,
  type SelectedLocationInitialState,
} from '../utils/generateInitialSelectedLocationState.ts';
import { isGroup } from '../utils/guards.ts';
import { GroupContent } from './components/GroupContent.tsx';
import { GroupHeader } from './components/GroupHeader.tsx';
import { GroupContentLegacy } from './GroupContentLegacy.tsx';
import { GroupContentRegionFirst } from './GroupContentRegionFirst.tsx';
import type { GroupContentBaseProps } from './groupContentTypes.ts';
import { useGroupListCallbacks } from './hooks/useGroupListCallbacks.ts';
import { type GroupListCriteriaFieldName, useGroupListMarketOperators } from './hooks/useGroupListMarketOperators.ts';
import { MarketOperatorRestrictionAlert } from './MarketOperatorRestrictionAlert.tsx';
import { SubGroupList } from './SubGroupList.tsx';

export type CriteriaFieldName = GroupListCriteriaFieldName;

export type GroupListProps = {
  icon?: React.ReactNode;
  title: string;
  fieldName: CriteriaFieldName;
  fields: FieldArrayWithId<AudienceForm, `${CriteriaFieldName}.groups`, 'id'>[];
  update: UseFieldArrayUpdate<AudienceForm, `${CriteriaFieldName}.groups`>;
  remove: UseFieldArrayRemove;
  append: UseFieldArrayAppend<AudienceForm, `${CriteriaFieldName}.groups`>;
  setDrawerState: Dispatch<SetStateAction<DrawerState>>;
  isEdit: boolean;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
  onExclusionsRemoved?: () => void;
  errorMessage?: string;
  onMarketUnionCoerced?: () => void;
  isListInteractionDisabled?: boolean;
  listInteractionDisabledMessage?: string;
};

export const GroupList = ({
  icon,
  title,
  fieldName,
  fields,
  update,
  remove,
  append,
  setDrawerState,
  onExclusionsRemoved,
  errorMessage,
  isEdit,
  isCreate,
  isCreateFromTemplate,
  onMarketUnionCoerced,
  isListInteractionDisabled = false,
  listInteractionDisabledMessage,
}: GroupListProps) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const { control, setValue, getValues } = useFormContext<AudienceForm>();
  const { isAllowed } = useIsAllowed();
  const { havePoM: disabledDueToDealRevenueMethod } = useDealsRevenueMethod();

  const isListBlocked = isListInteractionDisabled;
  const isAllowedToSeeLookalikeSection = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.LOOKALIKE_SEGMENTS_ENABLED));

  const initialSelectedLocationState = useMemo(() => generateInitialSelectedLocationState(fields), [fields]);

  const [selectedLocationSegment, setSelectedLocationSegment] =
    useState<SelectedLocationInitialState>(initialSelectedLocationState);

  const handleSelectedLocationSegmentChange = useCallback(
    (segment: SelectedLocationSegment, index: number | string) => {
      setSelectedLocationSegment(prev => ({
        ...prev,
        [index]: segment,
      }));
    },
    []
  );

  const listType = fieldName === IncludesGroupName ? ListType.INCLUDES : ListType.EXCLUDES;

  const observableGroups = useWatch({
    name: `${fieldName}.groups`,
    control,
  });

  const {
    groupsPath,
    shouldUseRegionFirstFlow,
    topLevelMarketOperatorReason,
    subgroupRestrictionReasonByParentIndex,
    observableGroupOperator,
    isIdentityGraphMatchType,
  } = useGroupListMarketOperators({ fieldName, isCreate, isCreateFromTemplate });

  const isGroupEditable = isEdit && !isListBlocked;
  const isOperatorLockedDueToDealRevenue = !!(isEdit && disabledDueToDealRevenueMethod);

  const listBannerMarketOperatorReason = useMemo(() => {
    if (topLevelMarketOperatorReason != null) {
      return topLevelMarketOperatorReason;
    }

    for (const subgroupReason of Object.values(subgroupRestrictionReasonByParentIndex)) {
      if (subgroupReason != null) {
        return subgroupReason;
      }
    }

    return null;
  }, [topLevelMarketOperatorReason, subgroupRestrictionReasonByParentIndex]);

  const enforceMarketUnionOperators = useCallback(
    (options?: EnforceMarketUnionOperatorsCallOptions) => {
      const wasMarketUnionCoerced = applyMarketForcedUnionOperators({
        fieldName,
        getValues,
        setValue,
        shouldUseRegionFirstFlow,
        accountId: getValues(AudienceFields.OrganizationId) ?? '',
        includeSellableMultipartner: options?.includeSellableMultipartner ?? false,
      });

      if (wasMarketUnionCoerced) {
        onMarketUnionCoerced?.();
      }
    },
    [fieldName, getValues, setValue, shouldUseRegionFirstFlow, onMarketUnionCoerced]
  );

  useEffect(() => {
    void observableGroups;
    // Keep operators in sync for structural edits (add/remove/move).
    enforceMarketUnionOperators({ includeSellableMultipartner: true });
  }, [observableGroups, enforceMarketUnionOperators]);

  const {
    isExpanded,
    toggle,
    setExpanded,
    handleToggleOperator,
    handleCloseModal,
    handleConfirmDelete,
    createSubgroupRemoveHandler,
    createAddSubgroupHandler,
    createRemoveGroupHandler,
    createSetSubgroupOperatorHandler,
  } = useGroupListCallbacks({
    fieldName,
    fields,
    update,
    remove,
    setValue,
    selectedIndex,
    setSelectedIndex,
    listType,
    onExclusionsRemoved,
    handleSelectedLocationSegmentChange,
    enforceMarketUnionOperators,
  });

  const typedUpdate = useCallback(
    (updateIndex: number, value: Group) => {
      update(updateIndex, value);

      // Region/country edits update groups via RHF field-array update;
      // enforce market rules here so user-triggered coercion can show the info modal.
      enforceMarketUnionOperators({ includeSellableMultipartner: true });
    },
    [update, enforceMarketUnionOperators]
  );

  return (
    <>
      <Stack
        direction="row"
        data-test={listType === ListType.INCLUDES ? 'includes-section' : 'excludes-section'}
        sx={{
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
          }}
        >
          {icon}
          <Typography variant="h3">{title}</Typography>
        </Stack>
        {fields.length > 1 && (
          <OperatorSelector
            isSubgroup={false}
            path={`${fieldName}.operator`}
            readOnly={!isEdit || isListBlocked || isOperatorLockedDueToDealRevenue}
            andOperatorDisabled={
              isEdit &&
              !isListBlocked &&
              !isOperatorLockedDueToDealRevenue &&
              shouldUseRegionFirstFlow &&
              topLevelMarketOperatorReason != null
            }
          />
        )}
      </Stack>
      {isListBlocked && listInteractionDisabledMessage && (
        <Alert severity="info" sx={{ mb: 2 }} data-test="group-list-interaction-blocked-alert">
          {listInteractionDisabledMessage}
        </Alert>
      )}
      {isEdit && shouldUseRegionFirstFlow && listBannerMarketOperatorReason != null && (
        <Box sx={{ mt: 2 }}>
          <MarketOperatorRestrictionAlert reason={listBannerMarketOperatorReason} />
        </Box>
      )}
      {fields.map((field, index) => {
        const groupToggleId = field.uuid ?? field.id;
        const isSubgroup = 'groups' in field;
        const isNotLastItem = fields.length - 1 > index;

        const structurallyEmpty = !extractNonEmptyGroup(field);
        const functionallyEmpty = isGroupReallyEmpty(field);
        const isEmptyGroup = structurallyEmpty || functionallyEmpty;

        const groupName = t('Group {count}', { count: index + 1 });
        const groupFieldName: GroupIndexFieldName = buildIndexedGroupFieldName(groupsPath, index);
        const groupOperatorPath = `${groupFieldName}.operator` as OperatorPath;

        const currentGroup = isGroup(field) ? { ...field, uuid: groupToggleId } : undefined;
        const hasLookalikeSegmentsOnEdit = !!currentGroup?.lookalike?.segments?.length;
        const shouldDisplayLookalikeSection = isAllowedToSeeLookalikeSection || hasLookalikeSegmentsOnEdit;
        const removeEnable = fields.length > 1 || listType === ListType.EXCLUDES;
        const selectedRegionCode = currentGroup?.region ?? null;
        const parentSubgroupMarketReason = subgroupRestrictionReasonByParentIndex[index] ?? null;

        const groupContentBaseProps: GroupContentBaseProps | null = currentGroup
          ? {
              field: currentGroup,
              groupFieldName,
              groupName,
              index,
              isEdit: isGroupEditable,
              isEmptyGroup,
              listType,
              setDrawerState,
              update: typedUpdate,
              isExpanded,
              toggle,
              setExpanded,
              selectedLocationSegment: selectedLocationSegment[index] ?? audienceLocation.Any,
              handleSelectedLocationSegmentChange,
              isIdentityGraphMatchType,
              shouldDisplayLookalikeSection: shouldDisplayLookalikeSection ?? false,
              isCreate,
            }
          : null;

        return (
          <React.Fragment key={field.id}>
            <ItemContainer
              isExclude={listType === ListType.EXCLUDES}
              dataTestId={`group-section-${groupName.toLowerCase()}`}
            >
              <GroupHeader
                title={groupName}
                path={groupOperatorPath}
                isEdit={isGroupEditable}
                isSubgroup={isSubgroup}
                isExpanded={isExpanded(groupToggleId)}
                isRemoveDisabled={!removeEnable || isListBlocked}
                onRemove={createRemoveGroupHandler(index, isEmptyGroup)}
                onExpandToggle={toggle(groupToggleId)}
                operatorSelectorReadOnly={isSubgroup ? !isGroupEditable || isOperatorLockedDueToDealRevenue : undefined}
                operatorAndDisabled={
                  isSubgroup && isGroupEditable && shouldUseRegionFirstFlow
                    ? parentSubgroupMarketReason != null
                    : undefined
                }
              />
              {isExpanded(groupToggleId) && (
                <>
                  <GroupContent>
                    {!isSubgroup &&
                      groupContentBaseProps &&
                      (shouldUseRegionFirstFlow ? (
                        <GroupContentRegionFirst {...groupContentBaseProps} selectedRegionCode={selectedRegionCode} />
                      ) : (
                        <GroupContentLegacy {...groupContentBaseProps} />
                      ))}

                    {errorMessage && (
                      <Box sx={{ mb: 2 }} data-test="group-error-message">
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'error.main',
                            display: 'block',
                          }}
                        >
                          {errorMessage}
                        </Typography>
                      </Box>
                    )}

                    {isSubgroup && (
                      <SubGroupList
                        parentIndex={index}
                        fieldPath={`${fieldName}.groups`}
                        removeSubgroup={createSubgroupRemoveHandler(index)}
                        setSubgroupOperator={createSetSubgroupOperatorHandler(index)}
                        listType={listType}
                        setDrawerState={setDrawerState}
                        isEdit={isGroupEditable}
                        shouldUseRegionFirstFlow={shouldUseRegionFirstFlow}
                        siblingOperatorRestrictionReason={parentSubgroupMarketReason}
                        isExpanded={isExpanded}
                        toggle={toggle}
                        setExpanded={setExpanded}
                        errorMessage={errorMessage}
                        onAfterSubgroupTreeMutation={enforceMarketUnionOperators}
                      />
                    )}
                  </GroupContent>

                  {isGroupEditable && !isSubgroup && (
                    <>
                      <Divider />
                      <Box
                        sx={{
                          p: 2,
                        }}
                      >
                        <Button
                          variant="text"
                          color="primary"
                          onClick={createAddSubgroupHandler(index, field)}
                          data-test={'subgroup-add-subgroup-button'}
                        >
                          {t('Add Subgroup')}
                        </Button>
                      </Box>
                    </>
                  )}
                </>
              )}
            </ItemContainer>
            {fields.length && isNotLastItem && (
              <OperatorChip
                operator={
                  observableGroupOperator === MatchingOperator.INTERSECTION ? OperatorType.AND : OperatorType.OR
                }
                disabled={isOperatorLockedDueToDealRevenue}
                {...(!isEdit ||
                isListBlocked ||
                isOperatorLockedDueToDealRevenue ||
                (shouldUseRegionFirstFlow && topLevelMarketOperatorReason != null)
                  ? { readonly: true as const }
                  : { onOperatorChange: handleToggleOperator })}
                dataTest={'group-operator-chip'}
              />
            )}
          </React.Fragment>
        );
      })}

      {isEdit && !isListBlocked && (
        <AddButton
          onAdd={() => {
            if (fields.length === 1) {
              setValue(`${fieldName}.operator`, MatchingOperator.UNION);
            }

            append({ uuid: uuid(), operator: MatchingOperator.INTERSECTION });
          }}
          text={listType === ListType.INCLUDES ? t('Add Group') : t('Add Another Group')}
          tooltipText={t(
            'Add a segment group to your audience, to be combined (Any) or intersected (All) with other segment groups'
          )}
        />
      )}
      <ConfirmationModal
        isOpen={selectedIndex !== null}
        onClose={handleCloseModal}
        onDelete={handleConfirmDelete}
        title={t('Remove Group')}
        item={t('Group {count}', { count: (selectedIndex ?? 0) + 1 })}
      />
    </>
  );
};
